export const M5_UNIT_1 = {
  id: "m5-u1-numbers",
  title: "Unidad 1: Large Numbers",
  topic: "Numbers and Statistics in Context",
  iconType: "numbers",
  grammarImage: "/grammar/grammar-m5-u1.jpg",
  grammarText: "Large numbers are used to talk about populations, distances, prices, and quantities. In English, we use commas to separate thousands (1,000), and add 'and' before the last two digits when saying numbers over 100. For example: 2,876 = 'two thousand, eight hundred and seventy-six'. Numbers don't take plural 's' after hundred, thousand, or million (NOT: three thousands). We use these numbers in real contexts like: 'The population of Tokyo is 14,000,000' or 'The distance from Earth to the Moon is 384,400 kilometers.'",

  sets: [
    // SET 1 - A2: Basic large numbers (hundreds)
    {
      id: "m5-u1-s1",
      title: "Set 1: Hundreds in Context",
      questions: [
        {
          type: "mc",
          q: "A laptop costs $825. How do you say it?",
          options: [
            "Eight hundred and twenty-five dollars",
            "Eight hundreds and twenty-five dollars",
            "Eighty-two and five dollars"
          ],
          a: "Eight hundred and twenty-five dollars",
          successtip: "✓ Correct! Add 'and' before the last two digits! 💻"
        },
        {
          type: "input",
          q: "The hotel has 450 rooms. Write: four ___ and fifty rooms.",
          a: "hundred",
          successtip: "✓ HUNDRED! No 's' after hundred when using numbers! 🏨"
        },
        {
          type: "mc",
          q: "The restaurant can seat 375 people. How do you say it?",
          options: [
            "Three hundred and seventy-five people",
            "Three hundreds and seventy-five people",
            "Thirty-seven and five people"
          ],
          a: "Three hundred and seventy-five people",
          successtip: "✓ That's a big restaurant! 🍽️"
        },
        {
          type: "match",
          q: "Match the prices to their words:",
          pairs: [
            { left: "$299", right: "Two hundred and ninety-nine dollars" },
            { left: "$675", right: "Six hundred and seventy-five dollars" },
            { left: "$940", right: "Nine hundred and forty dollars" }
          ],
          a: "completed",
          tip: "Remember: **hundred and** + last two digits! 💵"
        },
        {
          type: "input",
          q: "A concert ticket costs $185. Write: one hundred and eighty-___.",
          a: "five",
          successtip: "✓ FIVE! Enjoy the concert! 🎵"
        },
        {
          type: "order",
          q: "Ordena: '412 students attended the graduation'",
          words: ["four", "hundred", "and", "twelve", "students"],
          a: "four hundred and twelve students",
          tip: "Structure: number + **hundred and** + digits + noun 🎓"
        },
        {
          type: "mc",
          q: "The gym membership costs $560 per year. How do you say it?",
          options: [
            "Five hundred and sixty dollars",
            "Five hundreds and sixty dollars",
            "Fifty-six dollars"
          ],
          a: "Five hundred and sixty dollars",
          successtip: "✓ Stay fit! 💪"
        },
        {
          type: "input",
          q: "There are 728 pages in the book. Write: seven hundred and twenty-___.",
          a: "eight",
          successtip: "✓ EIGHT! That's a long book! 📚"
        },
        {
          type: "mc",
          q: "The distance from Montreal to Quebec City is 250 km. How do you say it?",
          options: [
            "Two hundred and fifty kilometers",
            "Twenty-five kilometers",
            "Two hundreds and fifty kilometers"
          ],
          a: "Two hundred and fifty kilometers",
          successtip: "✓ Montreal-Quebec es una ruta histórica! 🇨🇦"
        },
        {
          type: "order",
          q: "Ordena: 'The apartment rent is $895 monthly'",
          words: ["eight", "hundred", "and", "ninety-five", "dollars"],
          a: "eight hundred and ninety-five dollars",
          tip: "Monthly = mensual. Rent = renta/alquiler 🏠"
        }
      ]
    },

    // SET 2 - A2: Thousands in daily life
    {
      id: "m5-u1-s2",
      title: "Set 2: Thousands Around Us",
      questions: [
        {
          type: "mc",
          q: "A used car costs $18,500. How do you say it?",
          options: [
            "Eighteen thousand, five hundred dollars",
            "Eighteen thousands, five hundreds dollars",
            "One hundred eighty-five dollars"
          ],
          a: "Eighteen thousand, five hundred dollars",
          successtip: "✓ Used cars cost thousands! 🚗"
        },
        {
          type: "input",
          q: "The small town has 6,200 people. Write: six thousand, two ___.",
          a: "hundred",
          successtip: "✓ TWO HUNDRED! Small towns have thousands of people! 🏘️"
        },
        {
          type: "mc",
          q: "The university has 12,000 students. How do you say it?",
          options: [
            "Twelve thousand students",
            "Twelve thousands students",
            "One hundred and twenty students"
          ],
          a: "Twelve thousand students",
          successtip: "✓ That's a medium-sized university! 🎓"
        },
        {
          type: "match",
          q: "Match the contexts to realistic numbers:",
          pairs: [
            { left: "Concert tickets sold", right: "25,000" },
            { left: "New car price", right: "$30,000" },
            { left: "Marathon runners", right: "8,500" }
          ],
          a: "completed",
          tip: "Context helps you **understand** if a number makes sense! 🏃"
        },
        {
          type: "input",
          q: "The company has 4,750 employees. Write: four thousand, seven hundred and ___.",
          a: "fifty",
          successtip: "✓ FIFTY! Large companies have thousands of employees! 💼"
        },
        {
          type: "order",
          q: "Traduce: 'The stadium has 40,000 seats'",
          words: ["The", "stadium", "has", "forty", "thousand", "seats"],
          a: "forty thousand seats",
          tip: "40,000 = forty thousand (no 'and' for exact thousands) 🏟️"
        },
        {
          type: "mc",
          q: "The distance from Boston to Chicago is 1,600 km. How do you say it?",
          options: [
            "One thousand, six hundred kilometers",
            "Sixteen kilometers",
            "Sixteen thousand kilometers"
          ],
          a: "One thousand, six hundred kilometers",
          successtip: "✓ That's a long road trip! 🚙"
        },
        {
          type: "input",
          q: "A wedding costs $35,000. Write: thirty-five ___.",
          a: "thousand",
          successtip: "✓ THOUSAND! Weddings are expensive! 💒"
        },
        {
          type: "mc",
          q: "The library has 52,000 books. How do you say it?",
          options: [
            "Fifty-two thousand books",
            "Fifty-two thousands books",
            "Five hundred and twenty books"
          ],
          a: "Fifty-two thousand books",
          successtip: "✓ That's a lot of reading material! 📚"
        },
        {
          type: "order",
          q: "Ordena: 'The festival attracted 19,500 visitors'",
          words: ["nineteen", "thousand,", "five", "hundred", "visitors"],
          a: "nineteen thousand, five hundred visitors",
          tip: "Use comma after **thousand**, then continue with hundreds 🎉"
        }
      ]
    },

    // SET 3 - A2: Combining thousands and hundreds
    {
      id: "m5-u1-s3",
      title: "Set 3: Complex Thousands",
      questions: [
        {
          type: "mc",
          q: "Mount McKinley is 6,190 meters high. How do you say it?",
          options: [
            "Six thousand, one hundred and ninety meters",
            "Sixty-one hundred and ninety meters",
            "Six thousands, one hundred and ninety meters"
          ],
          a: "Six thousand, one hundred and ninety meters",
          successtip: "✓ It's the tallest mountain in North America! 🏔️"
        },
        {
          type: "input",
          q: "The town has 8,450 residents. Write: eight thousand, four hundred and ___.",
          a: "fifty",
          successtip: "✓ FIFTY! Residents = habitantes 🏘️"
        },
        {
          type: "mc",
          q: "A luxury car costs $67,800. How do you say it?",
          options: [
            "Sixty-seven thousand, eight hundred dollars",
            "Six hundred and seventy-eight dollars",
            "Sixty-seven thousands, eight hundreds dollars"
          ],
          a: "Sixty-seven thousand, eight hundred dollars",
          successtip: "✓ Luxury cars are very expensive! 🚗💎"
        },
        {
          type: "match",
          q: "Match the numbers to real contexts:",
          pairs: [
            { left: "Small university students", right: "8,450" },
            { left: "Library books", right: "34,800" },
            { left: "Company employees", right: "12,375" }
          ],
          a: "completed",
          tip: "**Complex thousands** use: thousand, + hundred and + digits 📊"
        },
        {
          type: "input",
          q: "The concert hall seats 5,280 people. Write: five thousand, two hundred and ___.",
          a: "eighty",
          successtip: "✓ EIGHTY! Big concert hall! 🎭"
        },
        {
          type: "order",
          q: "Ordena: 'The company has 23,600 employees'",
          words: ["twenty-three", "thousand,", "six", "hundred", "employees"],
          a: "twenty-three thousand, six hundred employees",
          tip: "Use comma after **thousand**, then continue with hundreds 💼"
        },
        {
          type: "mc",
          q: "The annual salary is $54,920. How do you say it?",
          options: [
            "Fifty-four thousand, nine hundred and twenty dollars",
            "Five hundred and forty-nine dollars",
            "Fifty-four thousands, nine hundreds dollars"
          ],
          a: "Fifty-four thousand, nine hundred and twenty dollars",
          successtip: "✓ Annual salary = salario anual 💰"
        },
        {
          type: "input",
          q: "There are 91,500 people at the stadium. Write: ninety-one thousand, five ___.",
          a: "hundred",
          successtip: "✓ HUNDRED! That's a packed stadium! ⚽"
        },
        {
          type: "mc",
          q: "The school has 3,765 students enrolled. How do you say it?",
          options: [
            "Three thousand, seven hundred and sixty-five students",
            "Thirty-seven thousand students",
            "Three thousands, seven hundreds students"
          ],
          a: "Three thousand, seven hundred and sixty-five students",
          successtip: "✓ Enrolled = inscrito/matriculado 📝"
        },
        {
          type: "order",
          q: "Traduce: 'The apartment costs $475,000'",
          words: ["four", "hundred", "and", "seventy-five", "thousand", "dollars"],
          a: "four hundred and seventy-five thousand dollars",
          tip: "Real estate in big cities is expensive! 🏢"
        }
      ]
    },

    // SET 4 - A2+: Introducing millions
    {
      id: "m5-u1-s4",
      title: "Set 4: Millions in Context",
      questions: [
        {
          type: "mc",
          q: "Toronto has a population of 2,900,000. How do you say it?",
          options: [
            "Two million, nine hundred thousand",
            "Two millions, nine hundreds thousands",
            "Twenty-nine hundred thousand"
          ],
          a: "Two million, nine hundred thousand",
          successtip: "✓ Toronto is Canada's largest city! 🇨🇦"
        },
        {
          type: "input",
          q: "The government budget is $150,000,000. Write: one hundred and ___ million dollars.",
          a: "fifty",
          successtip: "✓ FIFTY! Government budgets use millions! 💰"
        },
        {
          type: "mc",
          q: "Los Angeles has 4,000,000 people. How do you say it?",
          options: [
            "Four million people",
            "Four millions people",
            "Forty thousand people"
          ],
          a: "Four million people",
          successtip: "✓ LA is the second-largest city in the USA! 🌴"
        },
        {
          type: "match",
          q: "Match the cities to their populations:",
          pairs: [
            { left: "Chicago", right: "2,700,000" },
            { left: "Houston", right: "2,300,000" },
            { left: "Phoenix", right: "1,600,000" }
          ],
          a: "completed",
          tip: "**Large cities** have populations in the millions! 🌆"
        },
        {
          type: "input",
          q: "The company's revenue was $8,000,000. Write: eight ___.",
          a: "million",
          successtip: "✓ MILLION! Revenue = ingresos 📈"
        },
        {
          type: "order",
          q: "Traduce: 'The country has 50 million inhabitants'",
          words: ["The", "country", "has", "fifty", "million", "inhabitants"],
          a: "fifty million inhabitants",
          tip: "Million never takes 's' with numbers: fifty million, NOT millions 🌍"
        },
        {
          type: "mc",
          q: "The lottery jackpot is $25,000,000. How do you say it?",
          options: [
            "Twenty-five million dollars",
            "Twenty-five millions dollars",
            "Two hundred and fifty thousand dollars"
          ],
          a: "Twenty-five million dollars",
          successtip: "✓ That's a huge jackpot! 🎰"
        },
        {
          type: "input",
          q: "The movie earned $320,000,000. Write: three hundred and twenty ___.",
          a: "million",
          successtip: "✓ MILLION! Blockbuster movie! 🎬"
        },
        {
          type: "mc",
          q: "The national park covers 6,500,000 acres. How do you say it?",
          options: [
            "Six million, five hundred thousand acres",
            "Sixty-five thousand acres",
            "Six millions, five hundreds thousands acres"
          ],
          a: "Six million, five hundred thousand acres",
          successtip: "✓ Acres = acres (medida de tierra) 🌲"
        },
        {
          type: "order",
          q: "Ordena: 'The stadium renovation cost $180 million'",
          words: ["one", "hundred", "and", "eighty", "million", "dollars"],
          a: "one hundred and eighty million dollars",
          tip: "Renovation = renovación. Major projects cost millions! 🏗️"
        }
      ]
    },

    // SET 5 - A2+: Large populations and distances
    {
      id: "m5-u1-s5",
      title: "Set 5: Large Populations",
      questions: [
        {
          type: "mc",
          q: "Mexico City has 9,200,000 people. How do you say it?",
          options: [
            "Nine million, two hundred thousand",
            "Ninety-two hundred thousand",
            "Nine millions, two hundreds thousands"
          ],
          a: "Nine million, two hundred thousand",
          successtip: "✓ ¡La CDMX es una de las ciudades más grandes del mundo! 🇲🇽"
        },
        {
          type: "input",
          q: "New York City has 8,300,000 residents. Write: eight million, three hundred ___.",
          a: "thousand",
          successtip: "✓ THOUSAND! NYC is huge! 🗽"
        },
        {
          type: "mc",
          q: "The distance from Earth to the Moon is 384,400 km. How do you say it?",
          options: [
            "Three hundred and eighty-four thousand, four hundred kilometers",
            "Thirty-eight million kilometers",
            "Three million kilometers"
          ],
          a: "Three hundred and eighty-four thousand, four hundred kilometers",
          successtip: "✓ That's far! 🌙"
        },
        {
          type: "match",
          q: "Match the countries to their populations:",
          pairs: [
            { left: "Canada", right: "38,000,000" },
            { left: "Australia", right: "26,000,000" },
            { left: "Netherlands", right: "17,500,000" }
          ],
          a: "completed",
          tip: "**Population data** helps us understand country sizes! 🗺️"
        },
        {
          type: "input",
          q: "London has 9,000,000 inhabitants. Write: nine ___.",
          a: "million",
          successtip: "✓ MILLION! London is the UK capital! 🇬🇧"
        },
        {
          type: "order",
          q: "Ordena: 'Brazil has an area of 8,515,767 square kilometers'",
          words: ["eight", "million,", "five", "hundred", "and", "fifteen", "thousand"],
          a: "eight million, five hundred and fifteen thousand",
          tip: "Square kilometers = kilómetros cuadrados (km²) 🌳"
        },
        {
          type: "mc",
          q: "The Amazon rainforest covers 5,500,000 km². How do you say it?",
          options: [
            "Five million, five hundred thousand square kilometers",
            "Fifty-five thousand square kilometers",
            "Five millions square kilometers"
          ],
          a: "Five million, five hundred thousand square kilometers",
          successtip: "✓ The Amazon is the world's largest rainforest! 🌳"
        },
        {
          type: "input",
          q: "Tokyo has 14,000,000 people. Write: fourteen ___.",
          a: "million",
          successtip: "✓ MILLION! Tokyo es la ciudad más poblada del mundo! 🗼"
        },
        {
          type: "mc",
          q: "The Great Lakes contain 22,800,000 cubic kilometers of water. How do you say it?",
          options: [
            "Twenty-two million, eight hundred thousand",
            "Two hundred and twenty-eight thousand",
            "Twenty-two millions, eight hundreds thousands"
          ],
          a: "Twenty-two million, eight hundred thousand",
          successtip: "✓ The Great Lakes are massive! 🌊"
        },
        {
          type: "order",
          q: "Traduce: 'Shanghai has 27 million residents'",
          words: ["Shanghai", "has", "twenty-seven", "million", "residents"],
          a: "twenty-seven million residents",
          tip: "Residents = residentes/habitantes 🏙️"
        }
      ]
    },

    // SET 6 - A2+: Comparing large numbers
    {
      id: "m5-u1-s6",
      title: "Set 6: Comparing Statistics",
      questions: [
        {
          type: "mc",
          q: "Which city has a larger population: Tokyo (14,000,000) or London (9,000,000)?",
          options: [
            "Tokyo has a larger population",
            "London has a larger population",
            "They have the same population"
          ],
          a: "Tokyo has a larger population",
          successtip: "✓ Tokyo is much larger! 🗼"
        },
        {
          type: "input",
          q: "China has 1,400,000,000 people. Write: one billion, four hundred ___.",
          a: "million",
          successtip: "✓ MILLION! Billion = 1,000,000,000 🇨🇳"
        },
        {
          type: "mc",
          q: "Which is larger: 25,000,000 or 2,500,000?",
          options: [
            "25,000,000 (twenty-five million)",
            "2,500,000 (two million, five hundred thousand)",
            "They are the same"
          ],
          a: "25,000,000 (twenty-five million)",
          successtip: "✓ Count the zeros! More zeros = bigger! 🔢"
        },
        {
          type: "match",
          q: "Match the comparisons:",
          pairs: [
            { left: "Larger than 1 million", right: "5,000,000" },
            { left: "Smaller than 1 million", right: "750,000" },
            { left: "Between 10-20 million", right: "15,500,000" }
          ],
          a: "completed",
          tip: "Use **larger than** and **smaller than** to compare! 📊"
        },
        {
          type: "input",
          q: "India has 1,425,000,000 people. Write: one billion, four hundred and twenty-___ million.",
          a: "five",
          successtip: "✓ FIVE! India is the most populous country! 🇮🇳"
        },
        {
          type: "order",
          q: "Traduce: 'India's population is larger than the United States'",
          words: ["India's", "population", "is", "larger", "than", "the", "United", "States'"],
          a: "India's population is larger than the United States'",
          tip: "Use **'s** to show possession: country's population 🌏"
        },
        {
          type: "mc",
          q: "Compare: Canada (38,000,000) vs Poland (38,000,000). Which is true?",
          options: [
            "They have the same population",
            "Canada has a larger population",
            "Poland has a larger population"
          ],
          a: "They have the same population",
          successtip: "✓ Both have 38 million people! 🇨🇦🇵🇱"
        },
        {
          type: "input",
          q: "The USA has 331,000,000 people. Write: three hundred and thirty-one ___.",
          a: "million",
          successtip: "✓ MILLION! USA is the third most populous country! 🇺🇸"
        },
        {
          type: "mc",
          q: "Which country has MORE people: Brazil (215,000,000) or Mexico (128,000,000)?",
          options: [
            "Brazil has more people",
            "Mexico has more people",
            "They have the same"
          ],
          a: "Brazil has more people",
          successtip: "✓ Brazil is larger! 🇧🇷"
        },
        {
          type: "order",
          q: "Ordena: 'Nigeria's population grew from 200 million to 223 million'",
          words: ["two", "hundred", "million", "to", "two", "hundred", "and", "twenty-three", "million"],
          a: "two hundred million to two hundred and twenty-three million",
          tip: "**Grew from... to...** = creció de... a... 📈"
        }
      ]
    },

    // SET 7 - B1: Real-world economic statistics
    {
      id: "m5-u1-s7",
      title: "Set 7: Economic Data",
      questions: [
        {
          type: "mc",
          q: "A country's GDP is $456,000,000. How do you say it?",
          options: [
            "Four hundred and fifty-six million dollars",
            "Forty-six million dollars",
            "Four billions, fifty-six millions dollars"
          ],
          a: "Four hundred and fifty-six million dollars",
          successtip: "✓ GDP = Gross Domestic Product (PIB) 📊"
        },
        {
          type: "input",
          q: "The national debt is $1,500,000,000. Write: one billion, five hundred ___.",
          a: "million",
          successtip: "✓ MILLION! National debt = deuda nacional 💸"
        },
        {
          type: "mc",
          q: "The government spent $2,800,000,000 on infrastructure. How do you say it?",
          options: [
            "Two billion, eight hundred million dollars",
            "Twenty-eight billion dollars",
            "Two millions, eight hundreds dollars"
          ],
          a: "Two billion, eight hundred million dollars",
          successtip: "✓ Infrastructure = infraestructura (roads, bridges) 🛣️"
        },
        {
          type: "match",
          q: "Match economic terms to their typical values:",
          pairs: [
            { left: "City budget", right: "Billions" },
            { left: "Company profits", right: "Millions" },
            { left: "Average salary", right: "Thousands" }
          ],
          a: "completed",
          tip: "**Budget** = presupuesto. **Profits** = ganancias 💼"
        },
        {
          type: "input",
          q: "Exports reached $300,000,000,000. Write: three hundred ___.",
          a: "billion",
          successtip: "✓ BILLION! Exports = exportaciones 📦"
        },
        {
          type: "order",
          q: "Traduce: 'The company's revenue increased to $750 million'",
          words: ["seven", "hundred", "and", "fifty", "million", "dollars"],
          a: "seven hundred and fifty million dollars",
          tip: "**Revenue** = ingresos. **Increased** = aumentó 📈"
        },
        {
          type: "mc",
          q: "In a country of 50,000,000 workers, 5% are unemployed. How many is that?",
          options: [
            "Two million, five hundred thousand",
            "Twenty-five thousand",
            "Five million"
          ],
          a: "Two million, five hundred thousand",
          successtip: "✓ Calculate: 50M × 0.05 = 2.5M! 📉"
        },
        {
          type: "input",
          q: "The trade deficit is $680,000,000. Write: six hundred and eighty ___.",
          a: "million",
          successtip: "✓ MILLION! Trade deficit = déficit comercial 💹"
        },
        {
          type: "mc",
          q: "The stock market value is $18,000,000,000,000. How do you say it?",
          options: [
            "Eighteen trillion dollars",
            "Eighteen billion dollars",
            "Eighteen million dollars"
          ],
          a: "Eighteen trillion dollars",
          successtip: "✓ Trillion = 1,000,000,000,000 (un billón) 💰"
        },
        {
          type: "order",
          q: "Ordena: 'Foreign investment reached $125 billion last year'",
          words: ["one", "hundred", "and", "twenty-five", "billion", "dollars"],
          a: "one hundred and twenty-five billion dollars",
          tip: "**Foreign investment** = inversión extranjera 🌍"
        }
      ]
    },

    // SET 8 - B1: Scientific and geographic data
    {
      id: "m5-u1-s8",
      title: "Set 8: Science and Geography",
      questions: [
        {
          type: "mc",
          q: "The distance from the Sun to Earth is 149,600,000 km. How do you say it?",
          options: [
            "One hundred and forty-nine million, six hundred thousand kilometers",
            "Fourteen million kilometers",
            "One billion kilometers"
          ],
          a: "One hundred and forty-nine million, six hundred thousand kilometers",
          successtip: "✓ This is called an Astronomical Unit (AU)! ☀️"
        },
        {
          type: "input",
          q: "Mount Everest is 8,849 meters high. Write: eight thousand, eight hundred and forty-___.",
          a: "nine",
          successtip: "✓ NINE! Everest is the tallest mountain! 🏔️"
        },
        {
          type: "mc",
          q: "The Pacific Ocean covers 165,000,000 km². How do you say it?",
          options: [
            "One hundred and sixty-five million square kilometers",
            "One billion, six hundred and fifty thousand",
            "Sixteen million, five hundred thousand"
          ],
          a: "One hundred and sixty-five million square kilometers",
          successtip: "✓ The Pacific is the world's largest ocean! 🌊"
        },
        {
          type: "match",
          q: "Match geographic features to their measurements:",
          pairs: [
            { left: "Nile River length", right: "6,650 km" },
            { left: "Sahara Desert area", right: "9,000,000 km²" },
            { left: "Mariana Trench depth", right: "10,994 meters" }
          ],
          a: "completed",
          tip: "**Length** = longitud. **Depth** = profundidad 🗺️"
        },
        {
          type: "input",
          q: "The human body has 37,200,000,000,000 cells. Write: thirty-seven ___.",
          a: "trillion",
          successtip: "✓ TRILLION! Cells = células 🧬"
        },
        {
          type: "order",
          q: "Traduce: 'Scientists estimate the universe is 13 billion years old'",
          words: ["thirteen", "billion", "years", "old"],
          a: "thirteen billion years old",
          tip: "**Estimate** = estimar. **Universe** = universo 🌌"
        },
        {
          type: "mc",
          q: "Light travels at 299,792,458 meters per second. How do you say 299,792,458?",
          options: [
            "Two hundred and ninety-nine million, seven hundred and ninety-two thousand, four hundred and fifty-eight",
            "Two billion",
            "Twenty-nine million"
          ],
          a: "Two hundred and ninety-nine million, seven hundred and ninety-two thousand, four hundred and fifty-eight",
          successtip: "✓ That's the speed of light! 💡"
        },
        {
          type: "input",
          q: "The Milky Way has 100,000,000,000 stars. Write: one hundred ___.",
          a: "billion",
          successtip: "✓ BILLION! Our galaxy is huge! 🌌"
        },
        {
          type: "mc",
          q: "Antarctica covers 14,200,000 km². How do you say it?",
          options: [
            "Fourteen million, two hundred thousand square kilometers",
            "One hundred and forty-two thousand",
            "Fourteen billions square kilometers"
          ],
          a: "Fourteen million, two hundred thousand square kilometers",
          successtip: "✓ Antarctica is the coldest continent! ❄️"
        },
        {
          type: "order",
          q: "Ordena: 'The galaxy is 105,700 light-years in diameter'",
          words: ["one", "hundred", "and", "five", "thousand,", "seven", "hundred"],
          a: "one hundred and five thousand, seven hundred",
          tip: "**Diameter** = diámetro. **Light-year** = año luz 🔭"
        }
      ]
    },

    // SET 9 - B1: Historical and demographic data
    {
      id: "m5-u1-s9",
      title: "Set 9: Historical Statistics",
      questions: [
        {
          type: "mc",
          q: "In 1950, the world population was 2,536,000,000. How do you say it?",
          options: [
            "Two billion, five hundred and thirty-six million",
            "Twenty-five billion",
            "Two million, five hundred thousand"
          ],
          a: "Two billion, five hundred and thirty-six million",
          successtip: "✓ The world population has tripled since 1950! 🌍"
        },
        {
          type: "input",
          q: "During WWII, 75,000,000 people died. Write: seventy-five ___.",
          a: "million",
          successtip: "✓ MILLION! WWII was the deadliest conflict 😔"
        },
        {
          type: "mc",
          q: "The Roman Empire had 70,000,000 people at its peak. How do you say it?",
          options: [
            "Seventy million people",
            "Seventy millions people",
            "Seven hundred thousand people"
          ],
          a: "Seventy million people",
          successtip: "✓ Ancient Rome was huge! 🏛️"
        },
        {
          type: "match",
          q: "Match historical events to their numbers:",
          pairs: [
            { left: "Great Depression unemployed (US)", right: "15,000,000" },
            { left: "Black Death deaths (Europe)", right: "25,000,000" },
            { left: "Internet users in 2000", right: "361,000,000" }
          ],
          a: "completed",
          tip: "**Historical statistics** show the scale of past events 📚"
        },
        {
          type: "input",
          q: "In 1800, the world had 1,000,000,000 people. Write: one ___.",
          a: "billion",
          successtip: "✓ BILLION! Population has grown 8x since then! 📈"
        },
        {
          type: "order",
          q: "Traduce: 'The population increased from 1 billion to 8 billion'",
          words: ["one", "billion", "to", "eight", "billion"],
          a: "one billion to eight billion",
          tip: "**Increased from... to...** = aumentó de... a... 📊"
        },
        {
          type: "mc",
          q: "The Spanish Flu killed 50,000,000 people in 1918. How do you say it?",
          options: [
            "Fifty million people",
            "Fifty millions people",
            "Five hundred thousand people"
          ],
          a: "Fifty million people",
          successtip: "✓ It was a global pandemic 🦠"
        },
        {
          type: "input",
          q: "By 2050, the population will be 9,700,000,000. Write: nine billion, seven hundred ___.",
          a: "million",
          successtip: "✓ MILLION! UN projections for the future 🔮"
        },
        {
          type: "mc",
          q: "In 1960, there were 3,000,000,000 people on Earth. How do you say it?",
          options: [
            "Three billion people",
            "Three billions people",
            "Thirty million people"
          ],
          a: "Three billion people",
          successtip: "✓ Population doubled in just 39 years! 🌏"
        },
        {
          type: "order",
          q: "Ordena: 'Between 1900 and 2000, the population grew by 4.6 billion'",
          words: ["four", "billion,", "six", "hundred", "million"],
          a: "four billion, six hundred million",
          tip: "**Grew by** = creció en (amount of increase) 📈"
        }
      ]
    },

    // SET 10 - B1+: Integration and critical thinking
    {
      id: "m5-u1-s10",
      title: "Set 10: Analyzing Data",

      questions: [
        {
          type: "mc",
          q: "Country A: 50 million people, 500,000 km². Country B: 10 million people, 100,000 km². Which is true?",
          options: [
            "Both have the same population density (100 people/km²)",
            "Country A is more densely populated",
            "Country B is more densely populated"
          ],
          a: "Both have the same population density (100 people/km²)",
          successtip: "✓ Density = población ÷ área! 🧮"
        },
        {
          type: "input",
          q: "If GDP is $2,500,000,000,000 and population is 50,000,000, GDP per capita is $___.",
          a: "fifty thousand",
          successtip: "✓ FIFTY THOUSAND! Per capita = por persona 💵"
        },
        {
          type: "mc",
          q: "A city grew from 5,000,000 to 6,500,000. What was the increase?",
          options: [
            "One million, five hundred thousand (30% increase)",
            "Six million, five hundred thousand",
            "Five million"
          ],
          a: "One million, five hundred thousand (30% increase)",
          successtip: "✓ Increase = new value - old value! 📊"
        },
        {
          type: "match",
          q: "Match percentages to numbers (Total population: 200,000,000):",
          pairs: [
            { left: "10% of population", right: "20,000,000" },
            { left: "25% of population", right: "50,000,000" },
            { left: "50% of population", right: "100,000,000" }
          ],
          a: "completed",
          tip: "To find **percentage**: multiply (200M × 0.10 = 20M) 🔢"
        },
        {
          type: "input",
          q: "If 15% of 40,000,000 people live in urban areas, how many is that? Write: six ___.",
          a: "million",
          successtip: "✓ MILLION! 40M × 0.15 = 6M! 🏙️"
        },
        {
          type: "order",
          q: "Traduce: 'The data shows urban populations grew by 30 million'",
          words: ["thirty", "million"],
          a: "thirty million",
          tip: "**Urban** = urbano. **Grew by** = creció en 📈"
        },
        {
          type: "mc",
          q: "Company revenue: $500M → $750M. What percentage increase is this?",
          options: [
            "50% increase (two hundred and fifty million dollars)",
            "25% increase",
            "75% increase"
          ],
          a: "50% increase (two hundred and fifty million dollars)",
          successtip: "✓ Calculate: (250÷500) × 100 = 50%! 💹"
        },
        {
          type: "input",
          q: "If a country's debt is 80% of its $5,000,000,000,000 GDP, the debt is $___.",
          a: "four trillion",
          successtip: "✓ FOUR TRILLION! 5T × 0.80 = 4T! 💸"
        },
        {
          type: "mc",
          q: "Which represents better economic health: GDP per capita of $65,000 or $35,000?",
          options: [
            "$65,000 - higher per capita income",
            "$35,000 - lower is better",
            "Both are the same"
          ],
          a: "$65,000 - higher per capita income",
          successtip: "✓ Higher GDP per capita = wealthier country! 💰"
        },
        {
          type: "order",
          q: "Ordena: 'The study analyzed populations ranging from 10 million to 100 million'",
          words: ["ten", "million", "to", "one", "hundred", "million"],
          a: "ten million to one hundred million",
          tip: "**Ranging from... to...** = que van desde... hasta... 📊"
        }
      ]
    }
  ],

 // READING SET
// ===================================================================
// READING SET (10 preguntas)
// ===================================================================
Readingset: {
  id: "m5-u1-reading",
  title: "Reading: World Population Facts",
  category: "reading",
  questions: [
    {
      type: "passage",
      passage: `**The Growing World Population**

In 1800, the world population was approximately one billion people. By 1930, it had doubled to two billion. The population continued to grow rapidly during the 20th century. In 1960, there were three billion people on Earth. Just 14 years later, in 1974, the population reached four billion.`
    },
    {
      type: "mc",
      q: "What was the world population in 1800?",
      options: [
        "One billion",
        "Two billion",
        "Three billion"
      ],
      a: "One billion",
      successtip: "✓ In 1800, the world had only 1 billion people! 🌍"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 2: Current Population Leaders (Preguntas 2-4)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Today's Numbers**

Today, the world population is more than eight billion people. The most populous country is India, with 1,400,000,000 inhabitants, followed by China with 1,425,000,000 people. The United States has approximately 331,000,000 residents, making it the third most populous nation.`
    },
    {
      type: "mc",
      q: "Which country is currently the most populous according to the text?",
      options: [
        "India",
        "China",
        "United States"
      ],
      a: "India",
      successtip: "✓ India overtook China recently! 🇮🇳"
    },
    {
      type: "input",
      q: "Write China's population in words: 1,425,000,000 = one billion, four hundred and twenty-___ million.",
      a: "five",
      accept: { ignoreCase: true, ignorePunctuation: true },
      successtip: "✓ FIVE! China has 1.425 billion people! 🇨🇳"
    },
    {
      type: "input",
      q: "The USA has approximately ___ residents. (Write the number: 331,000,000)",
      a: "331,000,000",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["331 million", "331000000"] },
      successtip: "✓ USA is the third most populous! 🇺🇸"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 3: Unequal Growth (Japan vs Nigeria) (Preguntas 5-6)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Unequal Growth**

Population growth is not equal everywhere. Some countries, like Japan and Germany, have declining populations. Japan's population decreased from 128,000,000 in 2010 to 125,000,000 in 2023. Other countries, particularly in Africa, are experiencing rapid growth. Nigeria's population grew from 200,000,000 in 2019 to 223,000,000 in 2023.`
    },
    {
      type: "mc",
      q: "What is happening to Japan's population?",
      options: [
        "It is decreasing",
        "It is increasing rapidly",
        "It is staying the same"
      ],
      a: "It is decreasing",
      successtip: "✓ Japan has a declining population! 🇯🇵"
    },
    {
      type: "mc",
      q: "Nigeria's population grew from 200 million to how much in 2023?",
      options: [
        "223,000,000 (two hundred and twenty-three million)",
        "200,000,000 (two hundred million)",
        "250,000,000 (two hundred and fifty million)"
      ],
      a: "223,000,000 (two hundred and twenty-three million)",
      successtip: "✓ Nigeria is growing rapidly! 🇳🇬"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 4: Future & Rates (Preguntas 7-9)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**The Future**

Experts predict that by 2050, the global population will reach 9,700,000,000. However, the growth rate is slowing down. In the 1960s, the population grew by approximately 2.1% per year. Today, the growth rate is about 1% per year.`
    },
    {
      type: "mc",
      q: "According to the text, what will the population be in 2050?",
      options: [
        "9,700,000,000 (nine billion, seven hundred million)",
        "8,000,000,000 (eight billion)",
        "10,000,000,000 (ten billion)"
      ],
      a: "9,700,000,000 (nine billion, seven hundred million)",
      successtip: "✓ UN predictions for 2050! 🔮"
    },
    {
      type: "input",
      q: "What was the population growth rate in the 1960s? Write: two point one ___.",
      a: "percent",
      accept: { ignoreCase: true, ignorePunctuation: true, alts: ["%"] },
      successtip: "✓ PERCENT! Growth rate = tasa de crecimiento 📊"
    },
    {
      type: "mc",
      q: "What does the text say about population growth rate today?",
      options: [
        "It is slowing down to 1% per year",
        "It is increasing to 3% per year",
        "It is the same as the 1960s"
      ],
      a: "It is slowing down to 1% per year",
      successtip: "✓ Growth is slowing! 📉"
    },

    // ═══════════════════════════════════════════════════════════════════════
    // BLOQUE 5: Conclusion (Pregunta 10)
    // ═══════════════════════════════════════════════════════════════════════
    {
      type: "passage",
      passage: `**Why It Matters**

These numbers have important implications for resources, the environment, and the global economy. Understanding population statistics helps governments plan for the future and address challenges related to housing, food, healthcare, and education.`
    },
    {
      type: "mc",
      q: "According to the text, understanding population statistics helps governments:",
      options: [
        "Plan for housing, food, healthcare, and education",
        "Increase taxes",
        "Reduce population"
      ],
      a: "Plan for housing, food, healthcare, and education",
      successtip: "✓ Data helps future planning! 📋"
    }
  ]
},
  // REVIEW SET
  reviewSet: {
    id: "m5-u1-review",
    title: "Unit 1 Review: Large Numbers in Context",
    questions: [
      {
        type: "mc",
        q: "How do you say: 296,308?",
        options: [
          "Two hundred and ninety-six thousand, three hundred and eight",
          "Two millions, ninety-six thousands",
          "Twenty-nine million"
        ],
        a: "Two hundred and ninety-six thousand, three hundred and eight",
        successtip: "✓ Perfect! You got the structure right! ✅"
      },
      {
        type: "input",
        q: "The Eiffel Tower is 330 meters tall. Write: three hundred and ___.",
        a: "thirty",
        successtip: "✓ THIRTY! La Torre Eiffel! 🗼"
      },
      {
        type: "mc",
        q: "A house costs $475,000. How do you say it?",
        options: [
          "Four hundred and seventy-five thousand dollars",
          "Forty-seven thousand, five hundred dollars",
          "Four millions, seventy-five thousands dollars"
        ],
        a: "Four hundred and seventy-five thousand dollars",
        successtip: "✓ Real estate is expensive! 🏠"
      },
      {
        type: "match",
        q: "Match contexts to appropriate numbers:",
        pairs: [
          { left: "University students", right: "45,000" },
          { left: "City population", right: "3,500,000" },
          { left: "National budget", right: "$2,000,000,000" }
        ],
        a: "completed",
        tip: "**Context** helps you choose realistic numbers! 🎯"
      },
      {
        type: "input",
        q: "The Atlantic Ocean covers 106,460,000 km². Write: one hundred and six million, four hundred and ___ thousand.",
        a: "sixty",
        successtip: "✓ SIXTY! The Atlantic is huge! 🌊"
      },
      {
        type: "order",
        q: "Traduce: 'The company reported profits of $500 million'",
        words: ["five", "hundred", "million", "dollars"],
        a: "five hundred million dollars",
        tip: "**Reported** = reportó. **Profits** = ganancias 💼"
      },
      {
        type: "mc",
        q: "Which is grammatically correct?",
        options: [
          "Three thousand people",
          "Three thousands people",
          "Three thousands of people"
        ],
        a: "Three thousand people",
        successtip: "✓ Never add 's' to thousand with numbers! 📝"
      },
      {
        type: "input",
        q: "China has 1,425,000,000 people. Write: one billion, four hundred and twenty-___ million.",
        a: "five",
        successtip: "✓ FIVE! China is huge! 🇨🇳"
      },
      {
        type: "mc",
        q: "If exports increased from $80 billion to $100 billion, what's the increase?",
        options: [
          "Twenty billion dollars (25% increase)",
          "One hundred and eighty billion dollars",
          "Eighty billion dollars"
        ],
        a: "Twenty billion dollars (25% increase)",
        successtip: "✓ 100B - 80B = 20B. Then (20÷80)×100 = 25%! 📈"
      },
      {
        type: "match",
        q: "Match numbers to their forms:",
        pairs: [
          { left: "1,000,000", right: "One million" },
          { left: "1,000,000,000", right: "One billion" },
          { left: "1,000,000,000,000", right: "One trillion" }
        ],
        a: "completed",
        tip: "Million (6 zeros), billion (9), trillion (12)! 🔢"
      },
      {
        type: "order",
        q: "Ordena: 'Tokyo's population is larger than New York's'",
        words: ["Tokyo's", "population", "is", "larger", "than", "New", "York's"],
        a: "Tokyo's population is larger than New York's",
        tip: "Use **'s** for possession: city's population 🏙️"
      },
      {
        type: "mc",
        q: "The distance Earth-Mars is 225,000,000 km. How do you say it?",
        options: [
          "Two hundred and twenty-five million kilometers",
          "Twenty-two million kilometers",
          "Two billions kilometers"
        ],
        a: "Two hundred and twenty-five million kilometers",
        successtip: "✓ That's far! 🚀"
      },
      {
        type: "input",
        q: "Mount Everest is 8,849 meters. Write: eight thousand, eight hundred and forty-___.",
        a: "nine",
        successtip: "✓ NINE! Tallest mountain! 🏔️"
      },
      {
        type: "mc",
        q: "India has 1,400,000,000 people. Which is correct?",
        options: [
          "One billion, four hundred million",
          "One billions, four hundreds millions",
          "Fourteen billion"
        ],
        a: "One billion, four hundred million",
        successtip: "✓ India is the most populous country! 🇮🇳"
      },
      {
        type: "order",
        q: "Traduce: 'The population grew from 1 billion to 8 billion'",
        words: ["one", "billion", "to", "eight", "billion"],
        a: "one billion to eight billion",
        tip: "**Grew from... to...** = creció de... a... 📊"
      },
      {
        type: "mc",
        q: "FINAL CHALLENGE! How do you say: 4,125,025?",
        options: [
          "Four million, one hundred and twenty-five thousand, and twenty-five",
          "Forty-one million, twenty-five thousand",
          "Four thousand, one hundred and twenty-five"
        ],
        a: "Four million, one hundred and twenty-five thousand, and twenty-five",
        successtip: "✓ 🎉 PERFECTO! You mastered large numbers! 🏆🎊"
      }
    ]
  }
};
